import * as GAME from 'vibegame';
import { Transform, Parent, WorldTransform } from 'vibegame/transforms';
import { Renderer } from 'vibegame/rendering';
import { LineRenderer } from './line-renderer';
import { ScaleShaker } from './scale-shaker';

export const NeuralNetwork = GAME.defineComponent({
  layerSpacing: GAME.Types.f32,
  nodeSpacing: GAME.Types.f32,
  depthRows: GAME.Types.ui8,
  nodeSize: GAME.Types.f32,
  connections: GAME.Types.f32,
  initialized: GAME.Types.ui8,
});

export const NeuralNetworkLayer = GAME.defineComponent({
  network: GAME.Types.eid,
  index: GAME.Types.ui8,
  nodeCount: GAME.Types.ui8,
  collapse: GAME.Types.f32,
});

export const NeuralNetworkNode = GAME.defineComponent({
  layer: GAME.Types.eid,
  index: GAME.Types.ui8,
  originX: GAME.Types.f32,
  originY: GAME.Types.f32,
  originZ: GAME.Types.f32,
});

export const NeuralNetworkConnection = GAME.defineComponent({
  network: GAME.Types.eid,
  layerIndex: GAME.Types.ui8,
  startNode: GAME.Types.eid,
  endNode: GAME.Types.eid,
});

const LAYER_SIZES = [3, 5, 5, 2];

const neuralNetworkQuery = GAME.defineQuery([NeuralNetwork, Transform]);

const NeuralNetworkSetupSystem: GAME.System = {
  group: 'setup',
  update(state) {
    for (const eid of neuralNetworkQuery(state.world)) {
      if (NeuralNetwork.initialized[eid]) continue;
      NeuralNetwork.initialized[eid] = 1;

      const layerSpacing = NeuralNetwork.layerSpacing[eid] || 4;
      const nodeSpacing = NeuralNetwork.nodeSpacing[eid] || 2;
      const depthRows = NeuralNetwork.depthRows[eid] || 1;
      const nodeSize = NeuralNetwork.nodeSize[eid] || 0.6;

      const numLayers = LAYER_SIZES.length;
      const totalWidth = (numLayers - 1) * layerSpacing;
      const startX = -totalWidth / 2;

      const layerEntities: number[] = [];
      const nodeEntities: number[][] = [];

      for (let layerIdx = 0; layerIdx < numLayers; layerIdx++) {
        const nodesPerRow = LAYER_SIZES[layerIdx];
        const isHidden = layerIdx > 0 && layerIdx < numLayers - 1;
        const rows = isHidden ? depthRows : 1;
        const totalNodes = nodesPerRow * rows;

        const layerX = startX + layerIdx * layerSpacing;

        const layerEntity = state.createEntity();
        state.addComponent(layerEntity, Transform, { posX: layerX });
        state.addComponent(layerEntity, NeuralNetworkLayer, {
          network: eid,
          index: layerIdx,
          nodeCount: totalNodes,
          collapse: 0,
        });
        state.addComponent(layerEntity, Parent, { entity: eid });
        layerEntities.push(layerEntity);

        const totalHeight = (nodesPerRow - 1) * nodeSpacing;
        const totalDepth = (rows - 1) * nodeSpacing;
        const startY = -totalHeight / 2;
        const startZ = -totalDepth / 2;

        nodeEntities[layerIdx] = [];

        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < nodesPerRow; col++) {
            const y = startY + col * nodeSpacing;
            const z = startZ + row * nodeSpacing;

            const nodeEntity = state.createEntity();

            state.addComponent(nodeEntity, Transform, {
              posX: 0,
              posY: y,
              posZ: z,
              scaleX: 1,
              scaleY: 1,
              scaleZ: 1,
            });

            const isInput = layerIdx === 0;
            const isOutput = layerIdx === numLayers - 1;
            const color = isInput ? 0x44aa88 : isOutput ? 0xaa4488 : 0x4488ff;

            state.addComponent(nodeEntity, Renderer, {
              shape: 1,
              sizeX: nodeSize,
              sizeY: nodeSize,
              sizeZ: nodeSize,
              color,
              visible: 1,
            });

            state.addComponent(nodeEntity, NeuralNetworkNode, {
              layer: layerEntity,
              index: row * nodesPerRow + col,
              originX: 0,
              originY: y,
              originZ: z,
            });

            state.addComponent(nodeEntity, Parent, { entity: layerEntity });
            state.addComponent(nodeEntity, ScaleShaker);

            nodeEntities[layerIdx].push(nodeEntity);
          }
        }
      }

      for (let layerIdx = 0; layerIdx < numLayers - 1; layerIdx++) {
        const fromNodes = nodeEntities[layerIdx];
        const toNodes = nodeEntities[layerIdx + 1];

        for (const fromNode of fromNodes) {
          for (const toNode of toNodes) {
            const connectionEntity = state.createEntity();

            state.addComponent(connectionEntity, Transform, {});

            state.addComponent(connectionEntity, LineRenderer, {
              endX: 0,
              endY: 0,
              endZ: 0,
              color: 0x222222,
              visible: 1,
            });

            state.addComponent(connectionEntity, NeuralNetworkConnection, {
              network: eid,
              layerIndex: layerIdx,
              startNode: fromNode,
              endNode: toNode,
            });

            state.addComponent(connectionEntity, Parent, { entity: eid });
          }
        }
      }
    }
  },
};

const nodeQuery = GAME.defineQuery([NeuralNetworkNode, Transform]);

const NodeCollapseSystem: GAME.System = {
  group: 'simulation',
  update(state) {
    for (const eid of nodeQuery(state.world)) {
      const layerEid = NeuralNetworkNode.layer[eid];
      const collapse = NeuralNetworkLayer.collapse[layerEid];

      const originY = NeuralNetworkNode.originY[eid];
      const originZ = NeuralNetworkNode.originZ[eid];

      Transform.posY[eid] = originY * (1 - collapse);
      Transform.posZ[eid] = originZ * (1 - collapse);
    }
  },
};

const connectionQuery = GAME.defineQuery([NeuralNetworkConnection, LineRenderer]);

const ConnectionAnimationSystem: GAME.System = {
  group: 'simulation',
  after: [NodeCollapseSystem],
  update(state) {
    const numConnectionLayers = LAYER_SIZES.length - 1;

    for (const eid of connectionQuery(state.world)) {
      const network = NeuralNetworkConnection.network[eid];
      const layerIdx = NeuralNetworkConnection.layerIndex[eid];
      const startNode = NeuralNetworkConnection.startNode[eid];
      const endNode = NeuralNetworkConnection.endNode[eid];

      const startX = WorldTransform.posX[startNode];
      const startY = WorldTransform.posY[startNode];
      const startZ = WorldTransform.posZ[startNode];
      const endX = WorldTransform.posX[endNode];
      const endY = WorldTransform.posY[endNode];
      const endZ = WorldTransform.posZ[endNode];

      const t = NeuralNetwork.connections[network];
      const layerStart = layerIdx / numConnectionLayers;
      const layerEnd = (layerIdx + 1) / numConnectionLayers;
      const layerT = Math.max(0, Math.min(1, (t - layerStart) / (layerEnd - layerStart)));

      Transform.posX[eid] = startX;
      Transform.posY[eid] = startY;
      Transform.posZ[eid] = startZ;

      LineRenderer.endX[eid] = startX + (endX - startX) * layerT;
      LineRenderer.endY[eid] = startY + (endY - startY) * layerT;
      LineRenderer.endZ[eid] = startZ + (endZ - startZ) * layerT;
    }
  },
};

export const neuralNetworkRecipe: GAME.Recipe = {
  name: 'neural-network',
  components: ['neural-network', 'transform'],
};

export const NeuralNetworkPlugin: GAME.Plugin = {
  components: { NeuralNetwork, NeuralNetworkLayer, NeuralNetworkNode, NeuralNetworkConnection },
  systems: [NeuralNetworkSetupSystem, NodeCollapseSystem, ConnectionAnimationSystem],
  recipes: [neuralNetworkRecipe],
  config: {
    defaults: {
      'neural-network': {
        layerSpacing: 6,
        nodeSpacing: 3,
        depthRows: 3,
        nodeSize: 0.9,
        connections: 1,
        initialized: 0,
      },
      'neural-network-layer': {
        collapse: 0,
      },
    },
  },
};
